import React, { useEffect, useMemo } from 'react';
import { KpiCard, WeeklyBarChart } from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { KpiCard as KpiCardType, DailyDelivery } from './driverPerformanceTypes';
import { useDeliveriesStore } from '../../../store/deliveriesStore';
import styles from './DriverPerformance.module.css';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DriverPerformance: React.FC = () => {
  const performance = useDeliveriesStore((s) => s.performance);
  const error = useDeliveriesStore((s) => s.error);
  const fetchPerformance = useDeliveriesStore((s) => s.fetchPerformance);

  useEffect(() => { fetchPerformance(7); }, [fetchPerformance]);

  const performanceKpis = useMemo<KpiCardType[]>(() => {
    if (!performance) return [];
    return [
      { id: 'on-time', icon: 'check', label: 'On-time Delivery %', value: `${performance.on_time_rate.toFixed(1)}%`, color: 'green' },
      { id: 'completed', icon: 'clipboard', label: 'Completed vs Assigned', value: `${performance.delivered_stops}/${performance.total_stops}`, color: 'blue' },
      { id: 'failed', icon: 'alert', label: 'Failed Deliveries', value: String(performance.failed_stops).padStart(2, '0'), color: 'red' },
      { id: 'collections', icon: 'wallet', label: 'Total Collections', value: `$${Math.round(performance.total_collected).toLocaleString()}`, color: 'purple' },
    ];
  }, [performance]);

  const weeklyDeliveries = useMemo<DailyDelivery[]>(() => {
    if (!performance) return [];
    return performance.daily.map((d) => ({
      day: DAY_LABELS[new Date(d.date).getUTCDay()] || d.date.slice(5),
      count: d.delivered,
    }));
  }, [performance]);

  return (
    <div className={styles.container}>
      <AsyncBoundary
        loading={!performance}
        error={error}
        empty={!!performance && performance.total_stops === 0}
        emptyMessage="No performance data yet"
        onRetry={() => fetchPerformance(7)}
      >
        <div className={styles.kpiGrid}>
          {performanceKpis.map((kpi) => (
            <KpiCard key={kpi.id} kpi={kpi} />
          ))}
        </div>
        <WeeklyBarChart data={weeklyDeliveries} />
      </AsyncBoundary>
    </div>
  );
};

export const DriverPerformancePage: React.FC = () => <DriverPerformance />;

export { DriverPerformance };
export default DriverPerformance;
